import { useContext, createContext, ReactNode, useCallback, useState, useMemo, lazy, Suspense } from 'react';
import { EPhotoEditorConsumer } from 'shared/enums/EPhotoEditorConsumer';
const PhotoEditorV2ProviderWrapperLazy = lazy(
  () => import('../components/PhotoEditorV2ProviderWrapper/PhotoEditorV2ProviderWrapper'),
);

type EditProfilePhotoEditorProviderContextProps = {
  handlePhotoEditorOpen: (imageFiles: File[], consumerType: EPhotoEditorConsumer, isPrimary?: boolean) => void;
};

export const EditProfilePhotoEditorProviderContext = createContext<EditProfilePhotoEditorProviderContextProps>({
  handlePhotoEditorOpen: () => {},
});

export const EditProfilePhotoEditorProvider = ({ children }: { children: ReactNode }) => {
  const [showPhotoEditor, setShowPhotoEditor] = useState<boolean>(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [consumerType, setConsumerType] = useState<EPhotoEditorConsumer>(EPhotoEditorConsumer.PROFILE_SIDE_INFO);
  const [isPrimary, setIsPrimary] = useState<boolean | undefined>(undefined);

  const handlePhotoEditorOpen = useCallback(
    (imageFiles: File[], consumerType: EPhotoEditorConsumer, isPrimary?: boolean) => {
      if (imageFiles.length > 0) {
        setImageFiles(imageFiles);
        setConsumerType(consumerType);
        setShowPhotoEditor(true);
        setIsPrimary(isPrimary);
      }
    },
    [],
  );

  const handleCloseEditor = useCallback(() => {
    setShowPhotoEditor(false);
  }, []);

  const value = useMemo(() => ({ handlePhotoEditorOpen }), [handlePhotoEditorOpen]);

  return (
    <EditProfilePhotoEditorProviderContext.Provider value={value}>
      {showPhotoEditor ? (
        <Suspense fallback={''}>
          <PhotoEditorV2ProviderWrapperLazy
            showEditor={showPhotoEditor}
            handleEditor={handleCloseEditor}
            imageFiles={imageFiles}
            consumerType={consumerType}
            isPrimary={isPrimary}
          />
        </Suspense>
      ) : null}
      {children}
    </EditProfilePhotoEditorProviderContext.Provider>
  );
};

export const useEditProfilePhotoEditor = () => {
  const context = useContext(EditProfilePhotoEditorProviderContext);

  if (context === undefined) {
    throw new Error('You need context for this component');
  }

  return context;
};
